import { SurfacePagePanel } from '@/design-system';
import { cn } from '@/shared/cn';
import type { CapacityRead } from '@/versions/v1/schema/capacity';
import { bottleneckLadderProjection } from './BottleneckLadder.transform';
import { CapacityAxis } from './CapacityAxis';
import type { LadderStripCell, SelectableScenarioId } from './ThroughputTab.transform';

const STRIP_TONE: Record<LadderStripCell['tone'], string> = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
  neutral: 'text-ink-primary',
};

/**
 * The bottleneck ladder card: the four-cell hairline strip (here, how far,
 * what binds) over the one-axis figure (the ladder, the circuit ceilings and
 * the recovery curve on one stph scale).
 */
export function BottleneckLadder({
  read,
  strip,
  meta,
  feedLimited,
  selectedScenarioId,
}: {
  read: CapacityRead;
  strip: readonly LadderStripCell[];
  meta: string;
  feedLimited: boolean;
  selectedScenarioId: SelectableScenarioId;
}) {
  const ladder = bottleneckLadderProjection(read.bottleneckLadder);

  return (
    <SurfacePagePanel
      title="Bottleneck ladder"
      meta={<span className="text-xs tabular-nums text-muted">{meta}</span>}
      className="min-w-0 flex-1"
      contentClassName="pt-0"
    >
      <dl
        className={cn(
          'mb-1.5 grid border-b border-[var(--color-divider)]',
          feedLimited ? 'grid-cols-[1.6fr_1fr_1fr_1fr]' : 'grid-cols-4',
        )}
      >
        {strip.map((cell, index) => (
          <div
            key={cell.id}
            className={cn(
              'min-w-0 py-2',
              index === 0 ? 'pr-3' : 'px-3',
              index < strip.length - 1 && 'border-r border-[var(--color-divider)]',
            )}
          >
            <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.label}</dt>
            <dd className={cn('text-lg font-semibold leading-snug tabular-nums', STRIP_TONE[cell.tone])}>{cell.value}</dd>
            <dd className="text-xs tabular-nums text-muted">{cell.sub}</dd>
          </div>
        ))}
      </dl>
      <CapacityAxis read={read} ladder={ladder} selectedScenarioId={selectedScenarioId} />
    </SurfacePagePanel>
  );
}
